import React from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import AppBar from "@mui/material/AppBar";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import IconButton from "@mui/material/IconButton";
import HelpIcon from "@mui/icons-material/Help";
import ToggleNotificationsProductPurchase from "arena/components/ToggleNotificationsProductPurchase";
import DonationProgress from "arena/components/DonationProgress";
import Divider from "@mui/material/Divider";
import ReferalPromocode from "./ReferalPromocode";
import { useApolloClient, gql } from "@apollo/client";

const SUBSCRIPTION = "SUBSCRIPTION";
const PPV = "PPV";
const DONATION = "DONATION";
const PREFIX = "ProductPurchase";

const classes = {
  root: `${PREFIX}-root`,
  appBar: `${PREFIX}-appBar`,
  header: `${PREFIX}-header`,
  content: `${PREFIX}-content`,
};

const StyledCard = styled(Card)(({ theme }) => ({
  [`&.${classes.root}`]: {
    backdropFilter: "blur(12px)",
    backgroundColor: "rgba(255,255,255,.05)",
  },

  [`& .${classes.appBar}`]: {
    backdropFilter: "blur(12px)",
    backgroundColor: "rgba(0,0,0,.25)",
  },

  [`& .${classes.header}`]: {
    padding: theme.spacing(2),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(3),
    },
  },

  [`& .${classes.content}`]: {
    padding: theme.spacing(2),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(3),
    },
  },
}));

function ProductPurchase({ product, onClickHelpButton }) {
  const { t } = useTranslation("arena");
  const client = useApolloClient();

  if (!product?.viewerPurchase) return null;

  const hasSmobileService = product.viewerPurchase.smobileService;
  const showProductFinished = product?.finished;
  const subheader = showProductFinished
    ? t("productCover.finished", {
        date: product.finishAt,
      })
    : t("productCover.period", {
        startAt: product.startAt,
        finishAt: product.finishAt,
      });

  const titles = {
    SUBSCRIPTION: t("productPurchase.titleSubscription"),
    PPV: t("productPurchase.titlePPV"),
    DONATION: t("productPurchase.titleDonation"),
  };

  const onClickPayMore = () => {
    client.writeFragment({
      id: `Product:${product.id}`,
      fragment: gql`
        fragment MyProduct on Product {
          viewerPurchase {
            id
          }
        }
      `,
      data: {
        viewerPurchase: null,
      },
    });
  };

  return (
    <StyledCard className={classes.root} elevation={0}>
      <AppBar
        color="default"
        elevation={0}
        position="static"
        className={classes.appBar}
      >
        <CardHeader
          className={classes.header}
          title={titles[product.type]}
          subheader={
            product.type !== DONATION && !hasSmobileService
              ? subheader
              : undefined
          }
          titleTypographyProps={{
            variant: "h5",
          }}
          subheaderTypographyProps={{
            variant: "caption",
          }}
          action={
            <>
              <IconButton onClick={onClickHelpButton} size="large">
                <HelpIcon />
              </IconButton>
            </>
          }
        />
      </AppBar>
      <CardContent className={classes.content}>
        <Grid container spacing={2} justifyContent="center">
          {product?.donationProgress?.targetAmount && (
            <Grid item xs={12}>
              <DonationProgress product={product} />
            </Grid>
          )}
          <Grid item xs={12}>
            {!product.viewerPurchase.started && (
              <Typography
                component="div"
                variant="body2"
                color="textPrimary"
                gutterBottom
              >
                {t("productPurchase.notStarted", {
                  date: product.viewerPurchase.startAt,
                })}
              </Typography>
            )}

            {product.viewerPurchase.started && !hasSmobileService && (
              <Typography
                component="div"
                variant="body2"
                color="textPrimary"
                gutterBottom
              >
                {t("productPurchase.period", {
                  count: product.viewerPurchase.daysLeft,
                })}
              </Typography>
            )}

            {product.viewerPurchase.started && hasSmobileService && (
              <Typography
                component="div"
                variant="body2"
                color="textPrimary"
                gutterBottom
              >
                {t("productPurchase.smobilePeriod")}
              </Typography>
            )}

            {product.viewerPurchase.recurrent && (
              <Typography component="div" variant="body2" color="textPrimary">
                {product.viewerPurchase.renewSubscription
                  ? t("productPurchase.renewActive")
                  : t("productPurchase.renewCanceled")}
              </Typography>
            )}
          </Grid>

          {product.type === DONATION && (
            <Grid item xs={12}>
              <Button
                onClick={onClickPayMore}
                variant="contained"
                color="primary"
              >
                {t("productPurchase.oneMoreDonation")}
              </Button>
            </Grid>
          )}
          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <ToggleNotificationsProductPurchase
              productPurchase={product.viewerPurchase}
            />
          </Grid>
        </Grid>
      </CardContent>
      <ReferalPromocode product={product} classes={classes} />
    </StyledCard>
  );
}

ProductPurchase.propTypes = {
  onClickHelpButton: PropTypes.func.isRequired,
  product: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    shareUrl: PropTypes.string.isRequired,
    finished: PropTypes.bool,
    startAt: PropTypes.string.isRequired,
    finishAt: PropTypes.string.isRequired,
    composition: PropTypes.arrayOf(PropTypes.string).isRequired,
    type: PropTypes.oneOf([SUBSCRIPTION, PPV, DONATION]).isRequired,
    rightholder: PropTypes.shape({
      id: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      sellerInfo: PropTypes.string,
    }).isRequired,
    donationProgress: PropTypes.object,
    preview: PropTypes.shape({
      id: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    }),
    viewerPurchase: PropTypes.shape({
      id: PropTypes.string.isRequired,
      renewSubscription: PropTypes.bool,
      type: PropTypes.oneOf([SUBSCRIPTION, PPV, DONATION]).isRequired,
      recurrent: PropTypes.bool,
      started: PropTypes.bool,
      expired: PropTypes.bool,
      startAt: PropTypes.string.isRequired,
      createdAt: PropTypes.string.isRequired,
      expiredAt: PropTypes.string.isRequired,
      period: PropTypes.number.isRequired,
      daysLeft: PropTypes.number.isRequired,
      promocode: PropTypes.shape({
        code: PropTypes.string.isRequired,
      }),
      amount: PropTypes.shape({
        currency: PropTypes.string.isRequired,
      }),
      smobileService: PropTypes.shape({
        id: PropTypes.string.isRequired,
        isdn: PropTypes.string.isRequired,
        serviceId: PropTypes.string.isRequired,
        status: PropTypes.string.isRequired,
        timestamp: PropTypes.string.isRequired,
      }),
    }),
  }),
};

export default ProductPurchase;
